import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppFloat from '../../components/feature/WhatsAppFloat';
import Button from '../../components/base/Button';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Business Registration',
      description: 'LLC, Ltd, Corporation, and Nonprofit formation services with full compliance support.',
    },
    {
      icon: 'ri-government-line',
      title: 'Government Contracts',
      description: 'SAM.gov registration, CAGE codes, UEI processing, and professional proposal writing.',
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'Grant Writing',
      description: 'Expert grant writing for US, UK, and Canada funding opportunities.',
    },
    {
      icon: 'ri-computer-line',
      title: 'Web Design',
      description: 'Professional websites on Wix, WordPress, GoDaddy, Shopify, and more.',
    },
    {
      icon: 'ri-calculator-line',
      title: 'Tax & Compliance',
      description: 'EIN/TIN applications, tax filing, VAT registration, and ongoing compliance.',
    },
    {
      icon: 'ri-presentation-line',
      title: 'Business Strategy',
      description: 'Pitch decks, business plans, policy development, and strategic consulting.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white font-['Poppins']">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 min-h-screen flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-black"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/e60d4645cfef91f6ef3dd23a1750eb20.jpg')`
          }}
        ></div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center text-white">
            <div className="mb-8">
              <img 
                src="https://static.readdy.ai/image/73ef2aa8b67f0b1ba51e6666fcef6024/d6b3d5294f1861bfb4676f2aec75193f.jpeg"
                alt="Phoenix Achievers Team LLC"
                className="h-20 w-20 mx-auto mb-6 rounded-xl shadow-2xl"
              />
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              Consulting for Business Owners & Nonprofits
            </h1>
            
            <p className="text-xl lg:text-3xl mb-8 opacity-90 font-light">
              Empowering Business Owners & Nonprofits to Succeed
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12 text-lg lg:text-xl">
              <span className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">Business Registration</span>
              <span className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">Government Contracts</span>
              <span className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">Grant Writing</span>
              <span className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">Web Design & More</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white px-10 py-5 text-xl font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-3"></i>
                DM Us for Help 🚀
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Highlights */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Our Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive consulting solutions to help your business thrive in today's competitive landscape.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center text-red-600 hover:text-red-700 font-semibold transition-colors"
                  >
                    Learn More
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </Link>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/services">
                <Button className="bg-black hover:bg-gray-800 text-white px-8 py-4 text-lg font-semibold whitespace-nowrap">
                  View All Services
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-12 shadow-xl">
              <div className="flex justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-2xl"></i>
                ))}
              </div>
              <blockquote className="text-2xl lg:text-3xl text-gray-700 font-light leading-relaxed mb-8">
                "Phoenix Achievers Team transformed our business registration process. What seemed impossible became straightforward with their expertise. We secured our SAM registration and are now successfully bidding on federal contracts."
              </blockquote>
              <div className="flex items-center justify-center space-x-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20businesswoman%20headshot%2C%20confident%20female%20executive%20smiling%2C%20corporate%20portrait%20style%2C%20business%20leader%20testimonial%20photo&width=80&height=80&seq=testimonial-photo&orientation=squarish"
                  alt="Client Testimonial"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="text-left">
                  <p className="font-bold text-black text-lg">Sarah Johnson</p>
                  <p className="text-gray-600">CEO, TechStart Solutions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stats */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Our Track Record</h2>
              <p className="text-xl text-gray-600">Numbers that speak for our expertise and commitment to your success</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl p-8">
                <div className="text-4xl lg:text-5xl font-bold mb-2">300+</div>
                <div className="text-red-100 font-semibold">Successful Projects</div>
              </div>
              
              <div className="text-center bg-gradient-to-br from-black to-gray-800 text-white rounded-xl p-8">
                <div className="text-4xl lg:text-5xl font-bold mb-2">15+</div>
                <div className="text-gray-300 font-semibold">Countries Served</div>
              </div>
              
              <div className="text-center bg-gradient-to-br from-red-600 to-black text-white rounded-xl p-8">
                <div className="text-4xl lg:text-5xl font-bold mb-2">$5M+</div>
                <div className="text-red-100 font-semibold">Grants Secured</div>
              </div>
              
              <div className="text-center bg-gradient-to-br from-gray-700 to-black text-white rounded-xl p-8">
                <div className="text-4xl lg:text-5xl font-bold mb-2">98%</div>
                <div className="text-gray-300 font-semibold">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Global Reach</h2>
              <p className="text-xl text-gray-600">Serving clients across multiple continents with local expertise</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300">
                <div className="text-6xl mb-4">🇺🇸</div>
                <h3 className="text-2xl font-bold text-black mb-3">United States</h3>
                <p className="text-gray-600 mb-4">All 50 states coverage for business formation, government contracting, and compliance services.</p>
                <p className="text-red-600 font-bold">+1 469-738-0696</p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300">
                <div className="text-6xl mb-4">🇬🇧</div>
                <h3 className="text-2xl font-bold text-black mb-3">United Kingdom</h3>
                <p className="text-gray-600 mb-4">Company registration, VAT services, and business development across the UK.</p>
                <p className="text-red-600 font-bold">+44 7577336876</p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300">
                <div className="text-6xl mb-4">🇳🇬</div>
                <h3 className="text-2xl font-bold text-black mb-3">Nigeria</h3>
                <p className="text-gray-600 mb-4">Local business setup, CAC registration, and regulatory compliance services.</p>
                <p className="text-red-600 font-bold">09136628983</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-black mb-8">Why Choose Phoenix Achievers Team?</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2">Proven Expertise</h3>
                      <p className="text-gray-600">Over 300+ successful projects with clients worldwide, delivering consistent results across all service areas.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2">Fast Results</h3>
                      <p className="text-gray-600">Quick turnaround times without compromising quality. Most projects completed within days, not weeks.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2">24/7 Support</h3>
                      <p className="text-gray-600">Round-the-clock support through multiple channels including WhatsApp, email, and phone.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2">Global Experience</h3>
                      <p className="text-gray-600">International expertise with deep understanding of regulations in USA, UK, Nigeria, and beyond.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-br from-red-600 to-black rounded-2xl p-8 transform rotate-3">
                  <div className="bg-white rounded-xl p-4 transform -rotate-3">
                    <img 
                      src="https://static.readdy.ai/image/73ef2aa8b67f0b1ba51e6666fcef6024/0af980aff705918a3ae7f14bfa809c78.png"
                      alt="Phoenix Achievers Team - Professional Consulting Team"
                      className="w-full h-80 object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-black text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl lg:text-2xl mb-12 opacity-90">
              Join hundreds of successful businesses who have trusted Phoenix Achievers Team with their growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                onClick={() => window.open('https://www.fiverr.com/users/abiodun136', '_blank')}
                className="bg-red-600 text-white hover:bg-red-700 px-10 py-5 text-xl font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-3"></i>
                Book Free Consultation
              </Button>
              <Button 
                onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                className="bg-transparent border-3 border-white text-white hover:bg-white hover:text-red-600 px-10 py-5 text-xl font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-3"></i>
                WhatsApp Us Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Home;
